import React, { useEffect, useState } from 'react';
import ConciergeOtoStep from './ConciergeOtoStep';
import SecondaryConsentPrompt from './SecondaryConsentPrompt';
import { apiService } from '@/services/api';
import { otoAvailable, normalizeOffer, alreadyEnrolledFrom } from '@/data/conciergeOto';

// §concierge-oto — wraps a form's confirmation screen. When the post-payment
// one-time offer applies (flag on, NY, treatment visit created, card on file),
// the offer renders FIRST; "No thanks" / "Continue" then reveals the wrapped
// confirmation. Otherwise the children render untouched — so with the flag
// off every confirmation screen is byte-for-byte what it is today.
//
// `paymentResult` falls back to `answers.payment`, which the treatment forms
// store when the Square step completes.
const ConciergeOtoGate = ({ answers = {}, visitType = '', visitResult = null, paymentResult = null, children }) => {
  const [done, setDone] = useState(false);
  // §secondary-consent: set when the patient enrolled in a program on the offer.
  const [otoEnrolled, setOtoEnrolled] = useState(false);
  const payment = paymentResult || answers.payment || null;
  // Dr. Mavroson's consent is owed once she has paid for one of his programs —
  // the checkout add-on (rides on the treatment payment) or the offer below.
  const owesConsent = Boolean(payment?.conciergeAddOn) || otoEnrolled;
  const eligible = !done && otoAvailable({ answers, visitType, visitResult, paymentResult: payment });
  // The server prices and words the offer (BE #347). Fetch it once the patient
  // is eligible; any failure (404 while the flag is off, network) → no offer,
  // the confirmation renders as today. Never a spinner the patient can see:
  // the confirmation renders immediately and is REPLACED by the offer when
  // it lands within ~3 s.
  const [offer, setOffer] = useState(null);
  const [offerState, setOfferState] = useState('idle'); // idle | loading | ready | unavailable
  // Bumped to re-fetch (422 consent_changed). A separate counter, not
  // offerState, so a state change can't run this effect's cleanup and drop an
  // in-flight fetch.
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    if (!eligible) return undefined;
    let alive = true;
    setOfferState('loading');
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = setTimeout(() => ctrl && ctrl.abort(), 3000);
    apiService.getConciergeOtoOffer({ masterId: visitResult?.masterId, paymentId: payment?.paymentId })
      .then((raw) => {
        if (!alive) return;
        const o = normalizeOffer(raw);
        if (o) { setOffer(o); setOfferState('ready'); } else setOfferState('unavailable');
      })
      .catch(() => { if (alive) setOfferState('unavailable'); })
      .finally(() => clearTimeout(timer));
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, refreshTick]);
  // Keep showing the current offer while a refresh is in flight (consent_changed).
  const show = eligible && offer && offerState !== 'unavailable';
  if (!show) {
    // While the offer may still replace this screen, hold the pop-up back —
    // opening it only to have the offer swap it out a moment later is worse.
    const offerPending = eligible && (offerState === 'idle' || offerState === 'loading');
    if (!owesConsent || offerPending) return children;
    return (
      <>
        <SecondaryConsentPrompt className="max-w-xl mx-auto mt-6 mb-2" />
        {children}
      </>
    );
  }
  return (
    <ConciergeOtoStep
      answers={answers}
      visitType={visitType}
      visitResult={visitResult}
      paymentResult={payment}
      offer={offer}
      hide={alreadyEnrolledFrom(payment, visitResult)}
      onRefreshOffer={() => setRefreshTick((t) => t + 1)}
      onContinue={(r) => {
        if (r?.enrolled) setOtoEnrolled(true);
        setDone(true);
        try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) { /* jsdom */ }
      }}
    />
  );
};

export default ConciergeOtoGate;
